import { Overlay, Popover } from 'react-bootstrap'
import type { ReactNode } from 'react'
import type { OverlayProps } from 'react-bootstrap'
import { Scrollable } from '../Scrollable/Scrollable'

export type EditPopOverCardProps = {
  /** The popover's DOM id — a stored browser spec locates the card by its suffix. */
  id: string
  show: boolean
  /** The element the card is anchored beside. */
  target: HTMLElement | null
  placement?: OverlayProps['placement']
  children: ReactNode
}

// The anchored card an inline editor opens beside its display: the address editor and the customer editor render
// through it. No `rootClose`/`onHide` — the card is dismissed only by its own Save/Cancel/Escape, never by clicking
// away, or an in-progress edit would be silently discarded.
export const EditPopOverCard = ({ id, show, target, placement = 'auto', children }: EditPopOverCardProps) => (
  <Overlay
    show={show}
    target={target}
    placement={placement}
    // Popper's `preventOverflow` ships with `altAxis: false`, so a card taller than the room below the anchor hangs
    // past the viewport bottom. `altAxis` shifts it vertically into the viewport, `tether: false` lets that shift go
    // on once it detaches the card from the anchor, and `padding` keeps its edge off the viewport's.
    popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { altAxis: true, tether: false, padding: 8 } }] }}
  >
    {/* the `calc()` clamp keeps the 480px card inside a narrow viewport */}
    <Popover id={id} className="shadow-lg" style={{ width: 480, maxWidth: 'calc(100vw - 1rem)' }}>
      <Popover.Body>
        {/* Capped below 100vh (Scrollable's default): at full 100vh the card plus the body's padding is always taller
            than the viewport, so Popper's shift has no room and the bottom controls render off-screen. `showThumb`
            is an inverted prop name — false shows the card's own scrollbar. */}
        <Scrollable height="calc(100vh - 4rem)" showThumb={false}>
          {children}
        </Scrollable>
      </Popover.Body>
    </Popover>
  </Overlay>
)
